// Lightweight helpers to extract/log token usage from diverse backend shapes

export type TokenUsage = {
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
  inputTokens?: number; // alias for promptTokens for some vendors
  outputTokens?: number; // alias for completionTokens
};

export function extractUsage(data: any): TokenUsage | null {
  if (!data || typeof data !== 'object') return null;

  // Common shapes
  const u =
    (data as any).usage ||
    (data as any).token_usage ||
    (data as any).meta?.usage ||
    (data as any).provider_response?.usage ||
    null;

  const out: TokenUsage = {};

  const set = (k: keyof TokenUsage, v: any) => {
    const n = Number(v);
    if (Number.isFinite(n)) (out as any)[k] = n;
  };

  if (u && typeof u === 'object') {
    // OpenAI-like
    set('promptTokens', (u as any).prompt_tokens ?? (u as any).input_tokens);
    set('completionTokens', (u as any).completion_tokens ?? (u as any).output_tokens);
    set('totalTokens', (u as any).total_tokens);
    set('inputTokens', (u as any).input_tokens ?? (u as any).prompt_tokens);
    set('outputTokens', (u as any).output_tokens ?? (u as any).completion_tokens);
  }

  // Some backends may put numbers at root
  set('promptTokens', (data as any).prompt_tokens);
  set('completionTokens', (data as any).completion_tokens);
  set('totalTokens', (data as any).total_tokens);

  // If we found nothing meaningful, return null
  const hasAny =
    out.promptTokens != null ||
    out.completionTokens != null ||
    out.totalTokens != null ||
    out.inputTokens != null ||
    out.outputTokens != null;
  return hasAny ? out : null;
}

export function logUsage(
  usage: TokenUsage,
  context?: { provider?: string; model?: string; sessionId?: number; conversationId?: number | string; source?: string }
) {
  try {
    const prefix = context?.source ? `[AI Usage:${context.source}]` : '[AI Usage]';
    const base = {
      provider: context?.provider || null,
      model: context?.model || null,
      sessionId: context?.sessionId ?? null,
      conversationId: context?.conversationId ?? null,
    };
    // Compact log for console readability
    // eslint-disable-next-line no-console
    console.log(prefix, {
      ...base,
      promptTokens: usage.promptTokens ?? usage.inputTokens ?? null,
      completionTokens: usage.completionTokens ?? usage.outputTokens ?? null,
      totalTokens:
        usage.totalTokens ??
        ((usage.promptTokens ?? usage.inputTokens ?? 0) + (usage.completionTokens ?? usage.outputTokens ?? 0)) ||
        null,
    });
  } catch {
    // ignore
  }

  // Browser-only: persist minimal usage and broadcast event for UI
  try {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const prompt = usage.promptTokens ?? usage.inputTokens ?? 0;
      const completion = usage.completionTokens ?? usage.outputTokens ?? 0;
      const total = usage.totalTokens ?? (prompt + completion);
      const convId = context?.conversationId != null ? String(context.conversationId) : 'unknown';
      const sessId = context?.sessionId != null ? String(context.sessionId) : 'unknown';

      const totalsKey = `ai_usage_totals:${convId}`;
      const lastKey = `ai_usage_last:${convId}`;

      let totals: any = { prompt: 0, completion: 0, total: 0 };
      try { totals = JSON.parse(localStorage.getItem(totalsKey) || '{}') || totals; } catch {}
      totals.prompt = Number(totals.prompt || 0) + (Number.isFinite(prompt) ? prompt : 0);
      totals.completion = Number(totals.completion || 0) + (Number.isFinite(completion) ? completion : 0);
      totals.total = Number(totals.total || 0) + (Number.isFinite(total) ? total : 0);
      localStorage.setItem(totalsKey, JSON.stringify(totals));

      const last = {
        prompt,
        completion,
        total,
        at: Date.now(),
        provider: context?.provider || null,
        model: context?.model || null,
        sessionId: sessId,
        conversationId: convId,
        source: context?.source || null,
      };
      localStorage.setItem(lastKey, JSON.stringify(last));

      try {
        window.dispatchEvent(new CustomEvent('ai-usage', { detail: { totals, last } }));
      } catch {}

      // Also apply credit charge based on configured token rate (client-only)
      try {
        // Lazy import to avoid SSR issues
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        import('@/lib/credits').then((m) => {
          try {
            m.applyUsageCost(
              { prompt, completion, total },
              {
                model: context?.model || null,
                userId: undefined,
                conversationId: context?.conversationId ?? null,
              }
            );
          } catch {}
        }).catch(() => {});
      } catch {}
    }
  } catch {}
}
